import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Navigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useIsGeminiAdmin } from "@/hooks/useIsGeminiAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Building2, Info, Loader2, RefreshCw, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getWorkspacePlanDisplayName,
  getWorkspacePlanMonthlyPriceBrl,
} from "@/lib/stripe-plans";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const USD_BRL = 5.5;

type Daily = { day: string; calls: number; input_tokens: number; output_tokens: number; cost_usd: number };
type ByWorkspace = {
  workspace_id: string | null;
  workspace_name: string | null;
  workspace_plan?: string | null;
  owner_email: string | null;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
};
type ByType = { call_type: string; calls: number; cost_usd: number; input_tokens: number; output_tokens: number };
type ByKey = {
  api_key_name: string;
  calls: number;
  cost_usd: number;
  input_tokens?: number;
  output_tokens?: number;
};
type ByProvider = { provider: string; calls: number; cost_usd: number; input_tokens: number; output_tokens: number };
type MatrixRow = { workspace_id: string | null; workspace_name: string | null; call_type: string; api_key_name: string; provider?: string; calls: number; cost_usd: number };

type WorkspaceMatrixGroup = {
  workspace_id: string | null;
  workspace_name: string | null;
  total_calls: number;
  total_cost_usd: number;
  rows: MatrixRow[];
};

type Totals = {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cached_tokens?: number;
  cost_usd: number;
  failed_calls?: number;
  attributed_calls?: number;
  orphan_calls?: number;
  attributed_cost_usd?: number;
  orphan_cost_usd?: number;
};

interface UsageData {
  totals: Totals;
  daily: Daily[];
  by_workspace: ByWorkspace[];
  by_type: ByType[];
  by_key: ByKey[];
  by_provider?: ByProvider[];
  matrix: MatrixRow[];
}

const PROVIDER_LABELS: Record<string, string> = {
  gemini: "Gemini",
  openai: "OpenAI",
  lovable: "Lovable",
};

const API_KEY_LABELS: Record<string, string> = {
  agente_principal_openAI: "Agente principal (OpenAI)",
  AGENTE_PRINCIPAL_OPENAI: "Agente principal (OpenAI)",
  OPENAI_API_KEY: "OpenAI embeddings",
};

function apiKeyLabel(name: string) {
  return API_KEY_LABELS[name] ?? name;
}

const CALL_TYPE_LABELS: Record<string, string> = {
  chat_main: "Resposta principal do agente",
  chat_post_tool: "Resposta após ferramenta",
  follow_up: "Follow-up automático",
  intent_classifier: "Classificação de intenção",
  followup_need_classifier: "Classificação de follow-up",
  handoff_enforcer: "Regra de handoff",
  faq_enforcer: "Validação de FAQ",
  vision: "Análise de imagem",
  vision_url_ok: "Imagem por URL",
  vision_url_failed: "Imagem por URL (falha)",
  vision_inline_ok: "Imagem inline",
  vision_inline_failed: "Imagem inline (falha)",
  audio: "Transcrição de áudio",
  enhance: "Melhoria de mensagem",
  summarize: "Resumo de conversa",
  support: "Suporte interno",
  embedding_rag: "Embedding (busca RAG)",
  embedding_product: "Embedding (produto)",
  embedding_product_image: "Embedding (imagem de produto)",
  rag_query_expansion: "Expansão de consulta RAG",
};

const providerLabel = (p?: string) => PROVIDER_LABELS[p ?? "gemini"] ?? p ?? "—";
const callTypeLabel = (type: string) =>
  CALL_TYPE_LABELS[type] ?? type.replace(/_/g, " ");
const fmtUsd = (v: number) => `$${(Number(v) || 0).toFixed(4)}`;
const fmtBrl = (v: number) => `R$ ${((Number(v) || 0) * USD_BRL).toFixed(2)}`;
const fmtNum = (v: number) => (Number(v) || 0).toLocaleString("pt-BR");
const fmtPct = (v: number) => `${(Number(v) || 0).toFixed(1)}%`;

/** Números grandes em formato compacto para tabelas (ex.: 25,4 mi). */
function fmtCompact(n: number): string {
  const v = Number(n) || 0;
  if (v >= 1_000_000) {
    return `${(v / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi`;
  }
  if (v >= 10_000) {
    return `${(v / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`;
  }
  return fmtNum(v);
}

function periodDaysFromFilter(period: "7" | "30" | "today"): number {
  return period === "today" ? 1 : Number(period);
}

/** Custo LLM no período como % do valor proporcional do plano mensal (tabela Stripe). */
function planCostSharePercent(
  costUsd: number,
  planKey: string | null | undefined,
  periodDays: number,
): number | null {
  const planMonthlyBrl = getWorkspacePlanMonthlyPriceBrl(planKey);
  if (!planMonthlyBrl || periodDays <= 0) return null;
  const referencePlanBrl = planMonthlyBrl * (periodDays / 30);
  const costBrl = (Number(costUsd) || 0) * USD_BRL;
  return (costBrl / referencePlanBrl) * 100;
}

function planShareTitle(
  planKey: string | null | undefined,
  periodDays: number,
  percent: number,
): string {
  const planName = getWorkspacePlanDisplayName(planKey);
  const monthly = getWorkspacePlanMonthlyPriceBrl(planKey);
  const monthlyLabel = monthly
    ? monthly.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "";
  const periodLabel =
    periodDays === 1
      ? "1 dia"
      : periodDays === 30
        ? "30 dias"
        : `${periodDays} dias`;
  return `${fmtPct(percent)} do valor do plano ${planName} (${monthlyLabel}/mês) proporcional a ${periodLabel}.`;
}

function planShareTone(percent: number): string {
  if (percent >= 25) return "text-amber-800 dark:text-amber-300 font-semibold";
  if (percent >= 12) return "text-foreground font-medium";
  return "text-muted-foreground";
}

const isOrphanWorkspace = (row: { workspace_id: string | null }) => row.workspace_id === null;

function workspaceDisplayName(row: ByWorkspace) {
  if (isOrphanWorkspace(row)) return "Sem workspace (órfão)";
  return row.workspace_name || row.workspace_id?.slice(0, 8) || "—";
}

const chartConfig = {
  cost_usd: {
    label: "Custo USD",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

function formatDayLabel(day: string) {
  try {
    return format(parseISO(day), "dd/MM", { locale: ptBR });
  } catch {
    return day;
  }
}

function groupMatrixByWorkspace(matrix: MatrixRow[]): WorkspaceMatrixGroup[] {
  const byWorkspace = new Map<string, WorkspaceMatrixGroup>();

  for (const row of matrix) {
    const key = row.workspace_id ?? row.workspace_name ?? "unknown";
    if (!byWorkspace.has(key)) {
      byWorkspace.set(key, {
        workspace_id: row.workspace_id,
        workspace_name: row.workspace_name,
        total_calls: 0,
        total_cost_usd: 0,
        rows: [],
      });
    }
    const group = byWorkspace.get(key)!;
    group.rows.push(row);
    group.total_calls += row.calls;
    group.total_cost_usd += row.cost_usd;
  }

  return Array.from(byWorkspace.values())
    .sort((a, b) => b.total_cost_usd - a.total_cost_usd)
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort(
        (a, b) =>
          a.call_type.localeCompare(b.call_type, "pt-BR") ||
          a.api_key_name.localeCompare(b.api_key_name, "pt-BR"),
      ),
    }));
}

function MobileStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2 min-w-0">
      <p className="text-[11px] font-medium text-muted-foreground truncate">{label}</p>
      <p className="text-sm font-semibold tabular-nums mt-0.5 break-all">{value}</p>
      {sub ? <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p> : null}
    </div>
  );
}

function CostShareBar({ percent, className }: { percent: number; className?: string }) {
  const width = Math.min(Math.max(percent, 0), 100);
  return (
    <div
      className={cn("h-1.5 w-full min-w-[4rem] max-w-[8rem] rounded-full bg-muted overflow-hidden", className)}
      role="presentation"
      aria-hidden
    >
      <div
        className="h-full rounded-full bg-primary/80 transition-[width] duration-200 motion-reduce:transition-none"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function WorkspaceIdentity({
  row,
  rank,
  orphan,
}: {
  row: ByWorkspace;
  rank?: number;
  orphan: boolean;
}) {
  const name = workspaceDisplayName(row);
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      {rank != null ? (
        <span
          className={cn(
            "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold tabular-nums",
            rank <= 3 ? "bg-primary/12 text-primary" : "bg-muted text-muted-foreground",
          )}
        >
          {rank}
        </span>
      ) : (
        <span
          className={cn(
            "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
            orphan ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-muted text-muted-foreground",
          )}
        >
          <Building2 className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
      <div className="min-w-0">
        <p
          className={cn(
            "font-medium leading-snug break-words",
            orphan && "text-amber-900 dark:text-amber-200",
          )}
        >
          {name}
        </p>
        {row.owner_email ? (
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[14rem] sm:max-w-[20rem]" title={row.owner_email}>
            {row.owner_email}
          </p>
        ) : orphan ? (
          <p className="text-xs text-muted-foreground mt-0.5">Chamadas sem workspace vinculado</p>
        ) : null}
        {!orphan && row.workspace_plan ? (
          <p className="text-[10px] text-muted-foreground/90 mt-0.5">
            Plano {getWorkspacePlanDisplayName(row.workspace_plan)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function PlanShareCell({
  row,
  periodDays,
}: {
  row: ByWorkspace;
  periodDays: number;
}) {
  const percent = planCostSharePercent(row.cost_usd, row.workspace_plan, periodDays);
  if (percent == null) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const title = planShareTitle(row.workspace_plan, periodDays, percent);
  return (
    <div className="text-right" title={title}>
      <p className={cn("text-sm tabular-nums", planShareTone(percent))}>{fmtPct(percent)}</p>
      <p className="text-[10px] text-muted-foreground mt-0.5 hidden xl:block">do plano</p>
    </div>
  );
}

function WorkspaceUsageCard({
  row,
  rank,
  costShare,
  periodDays,
}: {
  row: ByWorkspace;
  rank: number;
  costShare: number;
  periodDays: number;
}) {
  const orphan = isOrphanWorkspace(row);
  const planShare = planCostSharePercent(row.cost_usd, row.workspace_plan, periodDays);
  return (
    <div
      className={cn(
        "rounded-lg border p-4 space-y-3",
        orphan && "border-amber-200/80 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <WorkspaceIdentity row={row} rank={rank} orphan={orphan} />
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold tabular-nums">{fmtUsd(row.cost_usd)}</p>
          <p className="text-[11px] text-muted-foreground tabular-nums">{fmtBrl(row.cost_usd)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{fmtPct(costShare)} do total</p>
        </div>
      </div>
      <CostShareBar percent={costShare} className="max-w-none" />
      <div className="grid grid-cols-2 gap-2">
        <MobileStat label="Chamadas à API" value={fmtNum(row.calls)} />
        <MobileStat
          label="% do plano"
          value={planShare != null ? fmtPct(planShare) : "—"}
          sub={
            planShare != null
              ? planShareTitle(row.workspace_plan, periodDays, planShare)
              : row.workspace_plan === "enterprise"
                ? "Plano Enterprise (valor sob consulta)"
                : undefined
          }
        />
        <MobileStat
          label="Tokens"
          value={`${fmtCompact(row.input_tokens)} entrada`}
          sub={`${fmtCompact(row.output_tokens)} saída`}
        />
        <MobileStat label="% do total" value={fmtPct(costShare)} />
      </div>
    </div>
  );
}

function WorkspaceUsageTable({
  rows,
  totalCost,
  periodDays,
}: {
  rows: ByWorkspace[];
  totalCost: number;
  periodDays: number;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">Nenhum dado no período selecionado.</p>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="w-[min(36%,20rem)]">Workspace</TableHead>
            <TableHead className="text-right w-[7rem]">Custo</TableHead>
            <TableHead className="text-right w-[6.5rem] hidden md:table-cell">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help border-b border-dotted border-muted-foreground/50">
                    % plano
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                  Custo de LLM no período em relação ao valor mensal do plano (tabela Stripe),
                  proporcional aos dias selecionados.
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="text-right w-[6rem] hidden lg:table-cell">Participação</TableHead>
            <TableHead className="text-right w-[5.5rem]">Chamadas</TableHead>
            <TableHead className="text-right w-[9rem] hidden xl:table-cell">Tokens entrada</TableHead>
            <TableHead className="text-right w-[9rem] hidden xl:table-cell">Tokens saída</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            const orphan = isOrphanWorkspace(row);
            const costShare = totalCost > 0 ? (row.cost_usd / totalCost) * 100 : 0;
            return (
              <TableRow
                key={row.workspace_id ?? row.workspace_name ?? index}
                className={cn(
                  "group",
                  orphan && "bg-amber-50/40 dark:bg-amber-950/15",
                )}
              >
                <TableCell className="align-top py-3">
                  <div className="space-y-2">
                    <WorkspaceIdentity row={row} rank={index + 1} orphan={orphan} />
                    <CostShareBar percent={costShare} className="lg:hidden" />
                  </div>
                </TableCell>
                <TableCell className="text-right align-top py-3">
                  <p className="font-semibold tabular-nums">{fmtUsd(row.cost_usd)}</p>
                  <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">{fmtBrl(row.cost_usd)}</p>
                </TableCell>
                <TableCell className="text-right align-top py-3 hidden md:table-cell">
                  <PlanShareCell row={row} periodDays={periodDays} />
                </TableCell>
                <TableCell className="text-right align-top py-3 hidden lg:table-cell">
                  <p className="text-sm tabular-nums font-medium">{fmtPct(costShare)}</p>
                  <CostShareBar percent={costShare} className="mt-1.5 ml-auto" />
                </TableCell>
                <TableCell className="text-right align-top py-3 tabular-nums text-sm">
                  {fmtNum(row.calls)}
                </TableCell>
                <TableCell
                  className="text-right align-top py-3 tabular-nums text-sm text-muted-foreground hidden xl:table-cell"
                  title={fmtNum(row.input_tokens)}
                >
                  {fmtCompact(row.input_tokens)}
                </TableCell>
                <TableCell
                  className="text-right align-top py-3 tabular-nums text-sm text-muted-foreground hidden xl:table-cell"
                  title={fmtNum(row.output_tokens)}
                >
                  {fmtCompact(row.output_tokens)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/20 px-4 py-2.5 text-[11px] text-muted-foreground">
        <span>{rows.length} workspace{rows.length === 1 ? "" : "s"}</span>
        <span>
          Custo total{" "}
          <span className="font-medium text-foreground tabular-nums">{fmtUsd(totalCost)}</span>
          {" · "}
          <span className="tabular-nums">{fmtBrl(totalCost)}</span>
        </span>
      </div>
    </div>
  );
}

function KeyUsageSection({
  rows,
  totalCost,
}: {
  rows: ByKey[];
  totalCost: number;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        Nenhuma chamada no período.
      </p>
    );
  }

  const sorted = [...rows].sort((a, b) => b.cost_usd - a.cost_usd);

  return (
    <>
      <div className="space-y-3 md:hidden">
        {sorted.map((row) => {
          const share = totalCost > 0 ? (row.cost_usd / totalCost) * 100 : 0;
          return (
            <div key={row.api_key_name} className="rounded-lg border p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug">{apiKeyLabel(row.api_key_name)}</p>
                  <p className="text-[10px] font-mono text-muted-foreground truncate mt-0.5">
                    {row.api_key_name}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold tabular-nums">{fmtUsd(row.cost_usd)}</p>
                  <p className="text-[11px] text-muted-foreground tabular-nums">{fmtBrl(row.cost_usd)}</p>
                </div>
              </div>
              <CostShareBar percent={share} className="max-w-none" />
              <div className="grid grid-cols-2 gap-2">
                <MobileStat label="Chamadas" value={fmtNum(row.calls)} />
                <MobileStat label="% do total" value={fmtPct(share)} />
                <MobileStat
                  label="Tokens"
                  value={`${fmtCompact(row.input_tokens ?? 0)} entrada`}
                  sub={`${fmtCompact(row.output_tokens ?? 0)} saída`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden md:block rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Chave</TableHead>
              <TableHead className="text-right">Chamadas</TableHead>
              <TableHead className="text-right">Tokens in</TableHead>
              <TableHead className="text-right">Tokens out</TableHead>
              <TableHead className="text-right">% custo</TableHead>
              <TableHead className="text-right">Custo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((row) => {
              const share = totalCost > 0 ? (row.cost_usd / totalCost) * 100 : 0;
              return (
                <TableRow key={row.api_key_name}>
                  <TableCell>
                    <p className="font-medium">{apiKeyLabel(row.api_key_name)}</p>
                    <p className="text-[10px] font-mono text-muted-foreground">{row.api_key_name}</p>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(row.calls)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCompact(row.input_tokens ?? 0)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCompact(row.output_tokens ?? 0)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtPct(share)}</TableCell>
                  <TableCell className="text-right">
                    <p className="font-semibold tabular-nums">{fmtUsd(row.cost_usd)}</p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">{fmtBrl(row.cost_usd)}</p>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

type BreakdownDimension = "type" | "key" | "provider";

type BreakdownItem = {
  id: string;
  label: string;
  calls: number;
  cost_usd: number;
  input_tokens?: number;
  output_tokens?: number;
  mono?: boolean;
};

const DIMENSION_LABELS: Record<BreakdownDimension, string> = {
  type: "Tipo de chamada",
  key: "Chave / API",
  provider: "Provedor",
};

function buildBreakdownItems(data: UsageData | null, dimension: BreakdownDimension): BreakdownItem[] {
  if (!data) return [];
  if (dimension === "type") {
    return [...(data.by_type || [])]
      .sort((a, b) => b.cost_usd - a.cost_usd)
      .map((r) => ({
        id: r.call_type,
        label: callTypeLabel(r.call_type),
        calls: r.calls,
        cost_usd: r.cost_usd,
        input_tokens: r.input_tokens,
        output_tokens: r.output_tokens,
      }));
  }
  if (dimension === "key") {
    return [...(data.by_key || [])]
      .sort((a, b) => b.cost_usd - a.cost_usd)
      .map((r) => ({
        id: r.api_key_name,
        label: apiKeyLabel(r.api_key_name),
        calls: r.calls,
        cost_usd: r.cost_usd,
        input_tokens: r.input_tokens,
        output_tokens: r.output_tokens,
        mono: !API_KEY_LABELS[r.api_key_name],
      }));
  }
  return [...(data.by_provider || [])]
    .sort((a, b) => b.cost_usd - a.cost_usd)
    .map((r) => ({
      id: r.provider,
      label: providerLabel(r.provider),
      calls: r.calls,
      cost_usd: r.cost_usd,
      input_tokens: r.input_tokens,
      output_tokens: r.output_tokens,
    }));
}

function UsageBreakdownExplorer({
  data,
  totals,
}: {
  data: UsageData | null;
  totals?: Totals;
}) {
  const [dimension, setDimension] = useState<BreakdownDimension>("type");
  const [selectedId, setSelectedId] = useState("");

  const items = useMemo(
    () => buildBreakdownItems(data, dimension),
    [data, dimension],
  );

  useEffect(() => {
    setSelectedId(items[0]?.id ?? "");
  }, [dimension, items]);

  const selected = items.find((item) => item.id === selectedId) ?? items[0];
  const totalCalls = totals?.calls ?? 0;
  const totalCost = totals?.cost_usd ?? 0;
  const shareCalls = totalCalls > 0 && selected ? (selected.calls / totalCalls) * 100 : 0;
  const shareCost = totalCost > 0 && selected ? (selected.cost_usd / totalCost) * 100 : 0;
  const avgCost = selected?.calls ? selected.cost_usd / selected.calls : 0;
  const hasTokens = selected?.input_tokens != null || selected?.output_tokens != null;

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        Nenhum dado no período selecionado.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="space-y-1.5 flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">Agrupar por</p>
          <Select
            value={dimension}
            onValueChange={(v) => setDimension(v as BreakdownDimension)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="type">{DIMENSION_LABELS.type}</SelectItem>
              <SelectItem value="key">{DIMENSION_LABELS.key}</SelectItem>
              <SelectItem value="provider">{DIMENSION_LABELS.provider}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex-[2] min-w-0">
          <p className="text-xs text-muted-foreground">Selecionar item</p>
          <Select value={selected?.id ?? ""} onValueChange={setSelectedId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Escolha um item" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {items.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.label} · {fmtUsd(item.cost_usd)} · {fmtNum(item.calls)} cham.
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selected ? (
        <div className="rounded-lg border bg-muted/20 p-4 space-y-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {DIMENSION_LABELS[dimension]}
            </p>
            <p
              className={cn(
                "text-base sm:text-lg font-semibold break-all mt-0.5",
                selected.mono && "font-mono text-sm sm:text-base",
              )}
            >
              {selected.label}
            </p>
            {dimension === "type" && selected.label !== selected.id ? (
              <p className="text-[11px] font-mono text-muted-foreground mt-1">{selected.id}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MobileStat label="Chamadas" value={fmtNum(selected.calls)} />
            <MobileStat
              label="Custo USD"
              value={fmtUsd(selected.cost_usd)}
              sub={fmtBrl(selected.cost_usd)}
            />
            {hasTokens ? (
              <>
                <MobileStat label="Tokens entrada" value={fmtNum(selected.input_tokens ?? 0)} />
                <MobileStat label="Tokens saída" value={fmtNum(selected.output_tokens ?? 0)} />
              </>
            ) : (
              <>
                <MobileStat label="Média / chamada" value={fmtUsd(avgCost)} />
                <MobileStat
                  label="% do custo total"
                  value={fmtPct(shareCost)}
                  sub={`${fmtPct(shareCalls)} das chamadas`}
                />
              </>
            )}
          </div>

          {hasTokens ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 border-t border-border/60">
              <MobileStat label="Média / chamada" value={fmtUsd(avgCost)} />
              <MobileStat label="% do custo total" value={fmtPct(shareCost)} />
              <MobileStat
                label="% das chamadas"
                value={fmtPct(shareCalls)}
                sub={
                  selected.input_tokens
                    ? `~${fmtNum(Math.round((selected.input_tokens + (selected.output_tokens ?? 0)) / selected.calls))} tok/cham`
                    : undefined
                }
              />
            </div>
          ) : null}

          <div className="space-y-2">
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Participação no custo</span>
              <span className="tabular-nums">{fmtPct(shareCost)}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.min(shareCost, 100)}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function WorkspaceMatrixDetailRow({
  row,
  groupCost,
  maxRowCost,
}: {
  row: MatrixRow;
  groupCost: number;
  maxRowCost: number;
}) {
  const share = groupCost > 0 ? (row.cost_usd / groupCost) * 100 : 0;
  const barWidth = maxRowCost > 0 ? (row.cost_usd / maxRowCost) * 100 : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.5fr)_5rem_5.5rem_7rem] gap-x-3 gap-y-2 items-start sm:items-center px-3 py-3 sm:px-4 border-b border-border/60 last:border-b-0 hover:bg-muted/25 transition-colors">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug break-words">{callTypeLabel(row.call_type)}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium">
            {providerLabel(row.provider)}
          </Badge>
          <span
            className="text-[10px] font-mono text-muted-foreground truncate max-w-[14rem]"
            title={row.api_key_name}
          >
            {apiKeyLabel(row.api_key_name)}
          </span>
        </div>
        <div className="mt-2 sm:hidden space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-xs tabular-nums">
            <span className="text-muted-foreground">{fmtNum(row.calls)} chamadas</span>
            <span className="font-medium">{fmtUsd(row.cost_usd)}</span>
          </div>
          <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
            <span>{fmtPct(share)} deste workspace</span>
            <span>{fmtBrl(row.cost_usd)}</span>
          </div>
          <CostShareBar percent={barWidth} className="max-w-none" />
        </div>
      </div>
      <p className="hidden sm:block text-right text-sm tabular-nums">{fmtNum(row.calls)}</p>
      <p className="hidden sm:block text-right text-sm tabular-nums text-muted-foreground">{fmtPct(share)}</p>
      <div className="hidden sm:block text-right min-w-0">
        <p className="text-sm font-semibold tabular-nums">{fmtUsd(row.cost_usd)}</p>
        <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">{fmtBrl(row.cost_usd)}</p>
        <CostShareBar percent={barWidth} className="mt-1.5 ml-auto max-w-[5.5rem]" />
      </div>
    </div>
  );
}

function WorkspaceMatrixAccordion({
  groups,
  workspaceById,
  totalCost,
  periodDays,
}: {
  groups: WorkspaceMatrixGroup[];
  workspaceById: Map<string, ByWorkspace>;
  totalCost: number;
  periodDays: number;
}) {
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState<string[]>(() =>
    groups.slice(0, 1).map((g, i) => g.workspace_id ?? g.workspace_name ?? String(i)),
  );

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => {
      const meta = group.workspace_id ? workspaceById.get(group.workspace_id) : undefined;
      const label = (group.workspace_name || group.workspace_id || "órfão").toLowerCase();
      const email = (meta?.owner_email || "").toLowerCase();
      const plan = getWorkspacePlanDisplayName(meta?.workspace_plan).toLowerCase();
      return label.includes(q) || email.includes(q) || plan.includes(q);
    });
  }, [groups, query, workspaceById]);

  useEffect(() => {
    if (openKeys.length === 0 && filteredGroups.length > 0) return;
    const validKeys = new Set(
      filteredGroups.map((g, i) => g.workspace_id ?? g.workspace_name ?? String(i)),
    );
    setOpenKeys((current) => current.filter((key) => validKeys.has(key)));
  }, [filteredGroups]);

  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        Nenhum dado no período selecionado.
      </p>
    );
  }

  const allKeys = filteredGroups.map((g, i) => g.workspace_id ?? g.workspace_name ?? String(i));

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {groups.length > 3 ? (
          <div className="relative flex-1 max-w-md">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar workspace, e-mail ou plano…"
              className="h-9 pl-8 text-sm"
              aria-label="Buscar workspace"
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {groups.length} workspace{groups.length === 1 ? "" : "s"} com detalhamento por tipo de chamada.
          </p>
        )}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setOpenKeys(allKeys)}
            disabled={filteredGroups.length === 0}
          >
            Expandir todos
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setOpenKeys([])}
          >
            Recolher
          </Button>
        </div>
      </div>

      {filteredGroups.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          Nenhum workspace corresponde à busca.
        </p>
      ) : (
        <Accordion type="multiple" value={openKeys} onValueChange={setOpenKeys} className="w-full space-y-2">
          {filteredGroups.map((group, index) => {
            const orphan = isOrphanWorkspace(group);
            const meta = group.workspace_id ? workspaceById.get(group.workspace_id) : undefined;
            const wsLabel = orphan
              ? "Sem workspace (órfão)"
              : group.workspace_name || group.workspace_id?.slice(0, 8) || "—";
            const groupKey = group.workspace_id ?? group.workspace_name ?? String(index);
            const globalIndex = groups.findIndex(
              (g) =>
                (g.workspace_id ?? g.workspace_name) === (group.workspace_id ?? group.workspace_name),
            );
            const rank = globalIndex >= 0 ? globalIndex + 1 : index + 1;
            const globalShare = totalCost > 0 ? (group.total_cost_usd / totalCost) * 100 : 0;
            const planShare = meta
              ? planCostSharePercent(group.total_cost_usd, meta.workspace_plan, periodDays)
              : null;
            const sortedRows = [...group.rows].sort((a, b) => b.cost_usd - a.cost_usd);
            const maxRowCost = sortedRows[0]?.cost_usd ?? 0;
            const topType = sortedRows[0];

            return (
              <AccordionItem
                key={groupKey}
                value={groupKey}
                className={cn(
                  "overflow-hidden rounded-lg border px-0 last:border-b",
                  orphan && "border-amber-200/80 bg-amber-50/25 dark:border-amber-900/50 dark:bg-amber-950/10",
                )}
              >
                <AccordionTrigger className="px-3 py-3 hover:no-underline sm:px-4 sm:py-3.5 [&[data-state=open]]:bg-muted/20">
                  <div className="flex flex-1 items-start gap-3 text-left min-w-0 pr-2">
                    <span
                      className={cn(
                        "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold tabular-nums",
                        index < 3 ? "bg-primary/12 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          "font-medium leading-snug break-words text-sm sm:text-base",
                          orphan && "text-amber-900 dark:text-amber-200",
                        )}
                      >
                        {wsLabel}
                      </p>
                      {meta?.owner_email ? (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[16rem] sm:max-w-[24rem]" title={meta.owner_email}>
                          {meta.owner_email}
                        </p>
                      ) : null}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
                          {sortedRows.length} tipo{sortedRows.length === 1 ? "" : "s"}
                        </Badge>
                        {meta?.workspace_plan ? (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium">
                            {getWorkspacePlanDisplayName(meta.workspace_plan)}
                          </Badge>
                        ) : null}
                        {topType ? (
                          <span className="text-[10px] text-muted-foreground truncate max-w-[12rem] sm:max-w-[18rem]">
                            Maior custo: {callTypeLabel(topType.call_type)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-right space-y-1 min-w-[6.5rem]">
                      <p className="text-sm font-semibold tabular-nums">{fmtUsd(group.total_cost_usd)}</p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">
                        {fmtBrl(group.total_cost_usd)}
                      </p>
                      <p className="text-[10px] text-muted-foreground tabular-nums">
                        {fmtNum(group.total_calls)} cham. · {fmtPct(globalShare)}
                      </p>
                      {planShare != null ? (
                        <p className={cn("text-[10px] tabular-nums", planShareTone(planShare))}>
                          {fmtPct(planShare)} do plano
                        </p>
                      ) : null}
                      <CostShareBar percent={globalShare} className="ml-auto max-w-[5.5rem]" />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-0">
                  <div className="border-t bg-muted/10">
                    <div className="hidden sm:grid sm:grid-cols-[minmax(0,1.5fr)_5rem_5.5rem_7rem] gap-x-3 px-4 py-2 text-[11px] font-medium text-muted-foreground border-b border-border/60">
                      <span>Tipo de chamada</span>
                      <span className="text-right">Chamadas</span>
                      <span className="text-right">% workspace</span>
                      <span className="text-right">Custo</span>
                    </div>
                    {sortedRows.map((row, i) => (
                      <WorkspaceMatrixDetailRow
                        key={`${row.call_type}-${row.provider ?? "gemini"}-${row.api_key_name}-${i}`}
                        row={row}
                        groupCost={group.total_cost_usd}
                        maxRowCost={maxRowCost}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}

export default function GeminiUsage() {
  const { isAdmin, loading: adminLoading } = useIsGeminiAdmin();
  const [period, setPeriod] = useState<"7" | "30" | "today">("30");
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { start, end } = useMemo(() => {
    const e = new Date();
    const s = new Date();
    if (period === "today") {
      // same day
    } else {
      s.setDate(s.getDate() - (Number(period) - 1));
    }
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    return { start: fmt(s), end: fmt(e) };
  }, [period]);

  const periodDays = periodDaysFromFilter(period);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: err } = await supabase.rpc("get_gemini_usage_admin" as any, {
        p_start: start,
        p_end: end,
      });
      if (err) throw err;
      setData(res as unknown as UsageData);
    } catch (e: any) {
      setError(e?.message || "Erro ao carregar uso");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, start, end]);

  const dailyChartData = useMemo(
    () =>
      (data?.daily || []).map((d) => ({
        ...d,
        dayLabel: formatDayLabel(d.day),
      })),
    [data?.daily],
  );

  const groupedMatrix = useMemo(
    () => groupMatrixByWorkspace(data?.matrix || []),
    [data?.matrix],
  );

  const workspaceRows = useMemo(
    () => [...(data?.by_workspace || [])].sort((a, b) => b.cost_usd - a.cost_usd),
    [data?.by_workspace],
  );

  const workspaceById = useMemo(() => {
    const map = new Map<string, ByWorkspace>();
    for (const row of workspaceRows) {
      if (row.workspace_id) map.set(row.workspace_id, row);
    }
    return map;
  }, [workspaceRows]);

  const costAttribution = useMemo(() => {
    const t = data?.totals;
    if (!t) return null;
    if (t.attributed_cost_usd != null) {
      return {
        total: t.cost_usd,
        attributed: t.attributed_cost_usd,
        orphan: t.orphan_cost_usd ?? 0,
      };
    }
    const orphanRow = data?.by_workspace?.find((r) => r.workspace_id === null);
    const orphanCost = orphanRow?.cost_usd ?? 0;
    return {
      total: t.cost_usd,
      attributed: t.cost_usd - orphanCost,
      orphan: orphanCost,
    };
  }, [data?.totals, data?.by_workspace]);

  if (adminLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const totals = data?.totals;
  const avgCostPerCall = totals?.calls ? totals.cost_usd / totals.calls : 0;
  const failureRate =
    totals?.failed_calls != null && totals.calls > 0
      ? (totals.failed_calls / totals.calls) * 100
      : null;

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold">Uso de LLM</h1>
          <p className="text-xs sm:text-sm text-muted-foreground break-words">
            Custo e volume de requisições à API (Gemini, OpenAI agente principal, embeddings, Lovable).
            <span className="hidden sm:inline"> Período: {start} → {end}.</span>
          </p>
          <p className="text-xs text-muted-foreground sm:hidden mt-0.5">
            {start} → {end}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-1 shrink-0">
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={(v) => setPeriod(v as any)}>
              <SelectTrigger className="w-full sm:w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center sm:text-right">
            Dados retidos por 30 dias.
          </p>
        </div>
      </div>

      <div
        className="flex gap-2.5 rounded-lg border border-border/80 bg-muted/30 px-3 py-2.5 sm:px-4 sm:py-3"
        role="note"
      >
        <Info className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
        <div className="min-w-0 text-xs sm:text-sm text-muted-foreground leading-relaxed">
          <p>
            <span className="font-medium text-foreground">Chamadas</span> são requisições à API — agente gpt-6-luna
            (chave agente_principal_openAI), classificadores Gemini, embeddings, retentativas e fallbacks.
            Tokens de entrada e saída entram no custo, mas o número de chamadas não corresponde ao de mensagens
            enviadas ao cliente.
          </p>
        </div>
      </div>

      {error && <Card><CardContent className="p-4 text-destructive">{error}</CardContent></Card>}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
        <Card>
          <CardHeader className="p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm text-muted-foreground">Chamadas à API</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-lg sm:text-2xl font-bold">{fmtNum(totals?.calls || 0)}</div>
            {failureRate != null ? (
              <div className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                {fmtPct(failureRate)} falhas
                {totals?.failed_calls != null ? ` (${fmtNum(totals.failed_calls)})` : null}
              </div>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm text-muted-foreground">Tokens entrada</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0 text-lg sm:text-2xl font-bold">{fmtNum(totals?.input_tokens || 0)}</CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm text-muted-foreground">Tokens saída</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0 text-lg sm:text-2xl font-bold">{fmtNum(totals?.output_tokens || 0)}</CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm text-muted-foreground">Tokens cacheados</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0 text-lg sm:text-2xl font-bold">{fmtNum(totals?.cached_tokens || 0)}</CardContent>
        </Card>
        <Card className="col-span-2 sm:col-span-1">
          <CardHeader className="p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm text-muted-foreground">Custo total</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-lg sm:text-2xl font-bold">{fmtUsd(totals?.cost_usd || 0)}</div>
            <div className="text-[10px] sm:text-xs text-muted-foreground">{fmtBrl(totals?.cost_usd || 0)}</div>
            {totals?.calls ? (
              <div className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                Média {fmtUsd(avgCostPerCall)}/chamada
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {costAttribution ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-lg border bg-card px-3 py-2.5 sm:px-4 sm:py-3">
            <p className="text-[11px] text-muted-foreground">Custo total</p>
            <p className="text-sm font-semibold tabular-nums mt-0.5">{fmtUsd(costAttribution.total)}</p>
          </div>
          <div className="rounded-lg border bg-card px-3 py-2.5 sm:px-4 sm:py-3">
            <p className="text-[11px] text-muted-foreground">Atribuído a workspaces</p>
            <p className="text-sm font-semibold tabular-nums mt-0.5">{fmtUsd(costAttribution.attributed)}</p>
          </div>
          <div
            className={cn(
              "rounded-lg border px-3 py-2.5 sm:px-4 sm:py-3",
              costAttribution.orphan > 0
                ? "border-amber-200/80 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20"
                : "bg-card",
            )}
          >
            <p className="text-[11px] text-muted-foreground">Sem workspace (órfão)</p>
            <p
              className={cn(
                "text-sm font-semibold tabular-nums mt-0.5",
                costAttribution.orphan > 0 && "text-amber-800 dark:text-amber-300",
              )}
            >
              {fmtUsd(costAttribution.orphan)}
            </p>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader className="p-4 sm:p-6"><CardTitle className="text-base sm:text-lg">Custo diário (USD)</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <ChartContainer config={chartConfig} className="h-52 sm:h-72 w-full aspect-auto">
            <AreaChart
              data={dailyChartData}
              margin={{ top: 10, right: 8, left: -8, bottom: 0 }}
            >
              <defs>
                <linearGradient id="geminiCostGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="dayLabel"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={44}
                tickFormatter={(v) => `$${Number(v).toFixed(2)}`}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => {
                      const day = payload?.[0]?.payload?.day as string | undefined;
                      if (!day) return "";
                      try {
                        return format(parseISO(day), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
                      } catch {
                        return day;
                      }
                    }}
                    formatter={(value) => fmtUsd(Number(value))}
                  />
                }
                cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area
                type="monotone"
                dataKey="cost_usd"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                fill="url(#geminiCostGradient)"
                dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 3, stroke: "hsl(var(--background))" }}
                activeDot={{ r: 5, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 2 }}
              />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Por chave / API</CardTitle>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Separa o custo do agente principal OpenAI (<span className="font-mono">agente_principal_openAI</span>,
            gpt-6-luna) dos embeddings (<span className="font-mono">OPENAI_API_KEY</span>) e das chaves Gemini.
          </p>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <KeyUsageSection rows={data?.by_key ?? []} totalCost={totals?.cost_usd ?? 0} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Por workspace</CardTitle>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Contas ordenadas por custo no período. A coluna <strong className="font-medium text-foreground">% plano</strong>{" "}
            compara o gasto de LLM com o valor mensal do plano (proporcional aos {periodDays === 1 ? "dia" : `${periodDays} dias`} selecionados).
          </p>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="space-y-3 md:hidden">
            {workspaceRows.map((row, index) => {
              const totalCost = totals?.cost_usd ?? 0;
              const costShare = totalCost > 0 ? (row.cost_usd / totalCost) * 100 : 0;
              return (
                <WorkspaceUsageCard
                  key={row.workspace_id ?? row.workspace_name ?? index}
                  row={row}
                  rank={index + 1}
                  costShare={costShare}
                  periodDays={periodDays}
                />
              );
            })}
            {workspaceRows.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">Nenhum dado no período.</p>
            )}
          </div>
          <div className="hidden md:block">
            <WorkspaceUsageTable rows={workspaceRows} totalCost={totals?.cost_usd ?? 0} periodDays={periodDays} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Análise por dimensão</CardTitle>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Escolha o agrupamento e o item para ver chamadas, tokens e participação no custo.
          </p>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <UsageBreakdownExplorer data={data} totals={totals} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Detalhamento por workspace</CardTitle>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Expanda cada conta para ver quais tipos de chamada consomem mais — ordenados por custo dentro do workspace.
          </p>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <WorkspaceMatrixAccordion
            groups={groupedMatrix}
            workspaceById={workspaceById}
            totalCost={totals?.cost_usd ?? 0}
            periodDays={periodDays}
          />
        </CardContent>
      </Card>
    </div>
  );
}
